import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FaChevronLeft, FaChevronRight, FaTimes, FaExpand, FaCompress, FaDownload, FaBars, FaList } from 'react-icons/fa';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './FlipbookReader.css';
import { trackEbookView, trackDuration } from '../utils/analytics';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FlipbookReader = ({
  pdfUrl,
  isOpen,
  onClose,
  courseTitle,
  courseId,                // Course/Ebook ID for analytics tracking
  chapters = [],           // Array of chapter objects with {_id, title, pdfFile, order}
  currentChapterIndex = 0, // Index of current chapter
  onChapterChange = null   // Callback when chapter changes
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState('');
  const [pageWidth, setPageWidth] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedChapterIndex, setSelectedChapterIndex] = useState(currentChapterIndex);
  const [showChapterSidebar, setShowChapterSidebar] = useState(false);

  // Analytics: Track time spent
  const startTimeRef = useRef(null);

  // Check if this is a multi-chapter ebook
  const isMultiChapter = chapters && chapters.length > 0;

  // Get current PDF URL (from chapters array or single pdfUrl prop)
  const currentPdfUrl = isMultiChapter && chapters[selectedChapterIndex]
    ? chapters[selectedChapterIndex].pdfFile
    : pdfUrl;

  // Debug: Log the PDF URL when component receives it
  useEffect(() => {
    if (currentPdfUrl && isOpen) {
      console.log('📖 FlipbookReader received PDF URL:', currentPdfUrl);
      console.log('📖 PDF URL type:', typeof currentPdfUrl);
      console.log('📖 Is Firebase URL?', currentPdfUrl.includes('storage.googleapis.com') || currentPdfUrl.includes('firebasestorage.googleapis.com'));
      if (isMultiChapter) {
        console.log('📚 Multi-chapter ebook - Chapter', selectedChapterIndex + 1, 'of', chapters.length);
      }
    }
  }, [currentPdfUrl, isOpen, isMultiChapter, selectedChapterIndex, chapters]);

  // Analytics: Track ebook/chapter views and time spent
  useEffect(() => {
    if (isOpen && courseId) {
      // Track previous chapter duration before changing
      if (startTimeRef.current !== null) {
        const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
        if (duration > 0) {
          const prevChapterId = isMultiChapter && chapters[selectedChapterIndex]
            ? chapters[selectedChapterIndex]._id
            : null;
          trackDuration(
            isMultiChapter ? 'chapter' : 'ebook',
            courseId,
            duration,
            prevChapterId
          );
        }
      }

      // Track view for current chapter/ebook
      const chapterId = isMultiChapter && chapters[selectedChapterIndex]
        ? chapters[selectedChapterIndex]._id
        : null;
      trackEbookView(courseId, chapterId);

      // Reset start time
      startTimeRef.current = Date.now();
    }

    // Cleanup: Track duration when component unmounts or reader closes
    return () => {
      if (startTimeRef.current !== null && courseId) {
        const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
        if (duration > 0) {
          const chapterId = isMultiChapter && chapters[selectedChapterIndex]
            ? chapters[selectedChapterIndex]._id
            : null;
          trackDuration(
            isMultiChapter ? 'chapter' : 'ebook',
            courseId,
            duration,
            chapterId
          );
        }
        startTimeRef.current = null;
      }
    };
  }, [isOpen, selectedChapterIndex, courseId, isMultiChapter, chapters]);

  // Reset page to 1 when chapter changes
  useEffect(() => {
    setCurrentPage(1);
    setNumPages(null);
  }, [selectedChapterIndex]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const updatePageWidth = () => {
      const container = document.querySelector('.flipbook-container');
      if (container) {
        const width = container.clientWidth;
        setPageWidth(isMobile ? width * 0.9 : Math.min(width * 0.4, 600));
      }
    };

    if (isOpen) {
      updatePageWidth();
      window.addEventListener('resize', updatePageWidth);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('resize', updatePageWidth);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  const onDocumentLoadSuccess = ({ numPages: loadedPages }) => {
    setNumPages((prevNumPages) => {
      // Only reset to page 1 if this is the first time loading (numPages was null)
      if (prevNumPages === null) {
        setCurrentPage(1);
      }
      return loadedPages;
    });
  };

  const goToPrevPage = useCallback(() => {
    if (currentPage > 1 && !isFlipping) {
      setIsFlipping(true);
      setFlipDirection('prev');
      setTimeout(() => {
        setCurrentPage(currentPage - 1);
        setIsFlipping(false);
        setFlipDirection('');
      }, 600);
    }
  }, [currentPage, isFlipping]);

  const goToNextPage = useCallback(() => {
    if (currentPage < numPages && !isFlipping) {
      setIsFlipping(true);
      setFlipDirection('next');
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setIsFlipping(false);
        setFlipDirection('');
      }, 600);
    }
  }, [currentPage, numPages, isFlipping]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = currentPdfUrl;
    const fileName = isMultiChapter && chapters[selectedChapterIndex]
      ? `${courseTitle || 'ebook'} - ${chapters[selectedChapterIndex].title}.pdf`
      : `${courseTitle || 'ebook'}.pdf`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Chapter navigation
  const goToPrevChapter = () => {
    if (selectedChapterIndex > 0) {
      const newIndex = selectedChapterIndex - 1;
      setSelectedChapterIndex(newIndex);
      if (onChapterChange) {
        onChapterChange(newIndex);
      }
    }
  };

  const goToNextChapter = () => {
    if (selectedChapterIndex < chapters.length - 1) {
      const newIndex = selectedChapterIndex + 1;
      setSelectedChapterIndex(newIndex);
      if (onChapterChange) {
        onChapterChange(newIndex);
      }
    }
  };

  const handleChapterSelect = (e) => {
    const newIndex = parseInt(e.target.value);
    setSelectedChapterIndex(newIndex);
    if (onChapterChange) {
      onChapterChange(newIndex);
    }
  };

  const handleChapterClick = (index) => {
    setSelectedChapterIndex(index);
    setShowChapterSidebar(false); // Close sidebar after selection
    if (onChapterChange) {
      onChapterChange(index);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'ArrowLeft') {
        goToPrevPage();
      } else if (e.key === 'ArrowRight') {
        goToNextPage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevPage, goToNextPage, onClose]);

  if (!isOpen) return null;

  return (
    <div className={`flipbook-modal ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="flipbook-overlay" onClick={onClose}></div>

      <div className="flipbook-container">
        {/* Header */}
        <div className="flipbook-header">
          <div className="flipbook-title">
            <h3>{courseTitle || 'Ebook Reader'}</h3>
            {isMultiChapter && (
              <div className="chapter-selector-wrapper">
                <select
                  value={selectedChapterIndex}
                  onChange={handleChapterSelect}
                  className="chapter-selector"
                >
                  {chapters.map((chapter, index) => (
                    <option key={chapter._id} value={index}>
                      Chapter {index + 1}: {chapter.title}
                    </option>
                  ))}
                </select>
                <div className="chapter-nav-buttons">
                  <button
                    onClick={goToPrevChapter}
                    disabled={selectedChapterIndex === 0}
                    className="chapter-nav-btn"
                    title="Previous Chapter"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="chapter-counter">
                    {selectedChapterIndex + 1} / {chapters.length}
                  </span>
                  <button
                    onClick={goToNextChapter}
                    disabled={selectedChapterIndex === chapters.length - 1}
                    className="chapter-nav-btn"
                    title="Next Chapter"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="flipbook-controls">
            {isMultiChapter && (
              <button
                className="control-btn"
                onClick={() => setShowChapterSidebar(!showChapterSidebar)}
                title="Chapters"
              >
                <FaList />
              </button>
            )}
            <button
              className="control-btn"
              onClick={handleDownload}
              title="Download PDF"
            >
              <FaDownload />
            </button>
            <button
              className="control-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
            <button className="control-btn close-btn" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Chapter Sidebar */}
        {isMultiChapter && showChapterSidebar && (
          <div className="chapter-sidebar">
            <div className="sidebar-header">
              <h4>Chapters</h4>
              <button
                className="sidebar-close"
                onClick={() => setShowChapterSidebar(false)}
                title="Close sidebar"
              >
                <FaTimes />
              </button>
            </div>
            <div className="sidebar-content">
              {chapters.map((chapter, index) => (
                <div
                  key={chapter._id}
                  className={`sidebar-chapter-item ${index === selectedChapterIndex ? 'active' : ''}`}
                  onClick={() => handleChapterClick(index)}
                >
                  <div className="sidebar-chapter-number">
                    {index + 1}
                  </div>
                  <div className="sidebar-chapter-info">
                    <h5>{chapter.title}</h5>
                    {chapter.pageCount > 0 && (
                      <span className="sidebar-chapter-pages">
                        {chapter.pageCount} pages
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Book Display */}
        <div className="flipbook-content">
          <button
            className="nav-btn prev-btn"
            onClick={goToPrevPage}
            disabled={currentPage <= 1 || isFlipping}
          >
            <FaChevronLeft />
          </button>

          <div className="book-wrapper">
            <div className={`book ${isFlipping ? `flipping-${flipDirection}` : ''}`}>
              {/* Left Page (for desktop two-page view) */}
              {!isMobile && currentPage > 1 && (
                <div className="page left-page">
                  <Document
                    file={currentPdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="page-loading">
                        <div className="spinner"></div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={currentPage - 1}
                      width={pageWidth}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                  </Document>
                  <div className="page-number">{currentPage - 1}</div>
                </div>
              )}

              {/* Right Page (or single page on mobile) */}
              <div className={`page ${isMobile ? 'single-page' : 'right-page'}`}>
                <Document
                  file={currentPdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className="page-loading">
                      <div className="spinner"></div>
                    </div>
                  }
                  error={
                    <div className="page-error">
                      <p>Failed to load PDF. Please try again.</p>
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPage}
                    width={pageWidth}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
                <div className="page-number">{currentPage}</div>
              </div>
            </div>
          </div>

          <button
            className="nav-btn next-btn"
            onClick={goToNextPage}
            disabled={currentPage >= numPages || isFlipping}
          >
            <FaChevronRight />
          </button>
        </div>

        {/* Footer with page counter */}
        <div className="flipbook-footer">
          <div className="page-info">
            Page {currentPage} of {numPages || '...'}
          </div>
          <div className="page-input-wrapper">
            <input
              type="number"
              min="1"
              max={numPages || 1}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= numPages && !isFlipping) {
                  setCurrentPage(page);
                }
              }}
              className="page-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlipbookReader;
